import moment from "moment-timezone";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";

import LeftArrow_3 from "../../assets/img/icons/LeftArrow_3.svg";
import RightArrow_3 from "../../assets/img/icons/RightArrow_3.svg";
import DatePicker from "../../components/SingleDatePicker/SingleDatePicker";
import { useScrollShadow } from "../../customHooks/useScrollShadow";
import { PrimaryButton, WhiteButton } from "../../styledComponents/buttons";
import {
  CommonFlex,
  Container,
  FilterLabel,
  HeaderContainer,
  IconArrow,
  InputWithLabelSection,
  PageTitle,
} from "../../styledComponents/common";
import { StickyFilterContainer } from "../../styledComponents/timesheet";
import JobSelectionModal from "./JobSelectionModal";
import OptimizationProgress from "./OptimizationProgress";
import RoutesList from "./RoutesList";
import {
  clearRouteOptimizationState,
  getRoutesByDate,
  optimizeRoutes,
} from "./routeOptimizationActions";
import {
  RouteOptimizationActions,
  RouteOptimizationFilterContainer,
} from "./routeOptimizationStyles";

function RouteOptimization({
  organization,
  routes,
  isLoadingRoutes,
  isOptimizing,
  selectedDate,
  optimizeRoutes,
  getRoutesByDate,
  clearRouteOptimizationState,
  selectedOrganization,
}) {
  const { t } = useTranslation();
  const showShadow = useScrollShadow(60);
  const [date, setDate] = useState(moment().format("YYYY-MM-DD"));
  const [selectedDateMoment, setSelectedDateMoment] = useState(moment());
  const [showJobSelectionModal, setShowJobSelectionModal] = useState(false);

  const handleFetchRoutes = () => {
    if (!organization?.id) return;

    getRoutesByDate({
      organization_id: organization.id,
      date: date,
    });
  };

  useEffect(() => {
    if (organization?.id) {
      getRoutesByDate({
        organization_id: organization.id,
        date: date,
      });
    }

    return () => {
      clearRouteOptimizationState();
    };
  }, [organization?.id, date, getRoutesByDate, clearRouteOptimizationState]);

  const handleOptimizeClick = () => {
    setShowJobSelectionModal(true);
  };

  const handleOptimizeWithSelection = (
    employeesWithJobs,
    startJobId,
    endJobId,
  ) => {
    if (!organization?.id) return;

    optimizeRoutes({
      organization_id: organization.id,
      date: date,
      mode: "custom",
      employees_jobs: employeesWithJobs,
      start_job_id: startJobId,
      end_job_id: endJobId,
    });
  };

  const handleDateChange = dateMoment => {
    if (dateMoment) {
      const newDate = dateMoment.format("YYYY-MM-DD");
      setDate(newDate);
      setSelectedDateMoment(dateMoment);

      if (organization?.id) {
        getRoutesByDate({
          organization_id: organization.id,
          date: newDate,
        });
      }
    }
  };

  const singleDateChange = direction => {
    if (direction === "right") {
      const newDate = moment(date).add(1, "days");
      setDate(newDate.format("YYYY-MM-DD"));
      setSelectedDateMoment(newDate);

      if (organization?.id) {
        getRoutesByDate({
          organization_id: organization.id,
          date: newDate.format("YYYY-MM-DD"),
        });
      }
    } else {
      const newDate = moment(date).subtract(1, "days");
      setDate(newDate.format("YYYY-MM-DD"));
      setSelectedDateMoment(newDate);

      if (organization?.id) {
        getRoutesByDate({
          organization_id: organization.id,
          date: newDate.format("YYYY-MM-DD"),
        });
      }
    }
  };

  const selectToday = () => {
    const today = moment();
    setDate(today.format("YYYY-MM-DD"));
    setSelectedDateMoment(today);

    if (organization?.id) {
      getRoutesByDate({
        organization_id: organization.id,
        date: today.format("YYYY-MM-DD"),
      });
    }
  };

  return (
    <div className="content" style={{ minHeight: "100vh" }}>
      <Container
        style={{
          padding: "24px",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
        }}
      >
        <HeaderContainer
          style={{
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "0px",
          }}
          margin="0px"
        >
          <PageTitle>Route Optimization</PageTitle>
          <RouteOptimizationActions>
            <PrimaryButton
              onClick={handleFetchRoutes}
              disabled={isLoadingRoutes}
              borderRadius="30px"
            >
              {isLoadingRoutes ? "Loading..." : "Refresh Routes"}
            </PrimaryButton>

            <PrimaryButton
              onClick={handleOptimizeClick}
              disabled={isOptimizing || isLoadingRoutes}
              borderRadius="30px"
            >
              {isOptimizing ? "Optimizing..." : "Optimize Routes"}
            </PrimaryButton>
          </RouteOptimizationActions>
        </HeaderContainer>

        {/* <StickyFilterContainer
          style={{
            width: "calc(100% + 48px)",
            marginLeft: "-24px",
            marginRight: "-24px",
            paddingLeft: "24px",
            paddingRight: "24px",
            marginBottom: "0px",
          }}
          showShadow={showShadow}
        > */}
        <HeaderContainer>
          <RouteOptimizationFilterContainer>
            <CommonFlex gap="4px" alignItems="end">
              <InputWithLabelSection>
                <FilterLabel>{t("date")}</FilterLabel>
                <DatePicker
                  id="route-date"
                  date={selectedDateMoment}
                  onDateChange={handleDateChange}
                  placeholder="Select date"
                  width="155px"
                />
              </InputWithLabelSection>
              <IconArrow
                onClick={() => {
                  if (!moment(date).isSameOrBefore(moment(), "day")) {
                    singleDateChange("left");
                  }
                }}
                disabled={moment(date).isSameOrBefore(moment(), "day")}
                padding="5px"
                size="39px"
                src={LeftArrow_3}
              />
              <IconArrow
                onClick={() => {
                  singleDateChange("right");
                }}
                padding="5px"
                size="39px"
                src={RightArrow_3}
              />
              <WhiteButton
                type="nav"
                style={{
                  height: "38px",
                  padding: "0px 15px",
                }}
                onClick={selectToday}
                borderRadius="6px"
              >
                Today
              </WhiteButton>
            </CommonFlex>
          </RouteOptimizationFilterContainer>
        </HeaderContainer>

        <RoutesList
          routes={routes}
          isLoading={isLoadingRoutes}
          organizationId={organization?.id}
          selectedDate={date}
        />

        {isOptimizing && <OptimizationProgress />}

        {showJobSelectionModal && (
          <JobSelectionModal
            organizationId={organization?.id}
            selectedDate={date}
            onClose={() => setShowJobSelectionModal(false)}
            onOptimize={handleOptimizeWithSelection}
            organization={selectedOrganization}
          />
        )}
      </Container>
    </div>
  );
}

const mapStateToProps = state => ({
  organization: state.organization?.selectedOrganization,
  routes: state.RouteOptimization.routes,
  isLoadingRoutes: state.RouteOptimization.isLoadingRoutes,
  isOptimizing: state.RouteOptimization.isOptimizing,
  selectedDate: state.RouteOptimization.selectedDate,
  selectedOrganization: state.organization?.selectedOrganization,
});

const mapDispatchToProps = {
  optimizeRoutes,
  getRoutesByDate,
  clearRouteOptimizationState,
};

export default connect(mapStateToProps, mapDispatchToProps)(RouteOptimization);
